import React, { useEffect } from 'react';
import { Submission, SubmissionStatus } from '../api/client';
import { ModalShell } from './ui';

interface SubmissionDetailProps {
  submission: Submission;
  formName: string;
  onClose: () => void;
  onStatusChange?: (action: 'approve' | 'flag' | 'spam') => void;
  onDelete?: () => void;
  onPrev?: () => void;
  onNext?: () => void;
  hasPrev?: boolean;
  hasNext?: boolean;
}

const STATUS_CLASS: Record<SubmissionStatus, string> = {
  pending: 'status-badge-neutral',
  approved: 'status-approved',
  flagged: 'status-flagged',
  spam: 'status-spam',
};

const SubmissionDetail: React.FC<SubmissionDetailProps> = ({
  submission,
  formName,
  onClose,
  onStatusChange,
  onDelete,
  onPrev,
  onNext,
  hasPrev = false,
  hasNext = false,
}) => {
  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString();
  };

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowLeft' && hasPrev && onPrev) onPrev();
      if (e.key === 'ArrowRight' && hasNext && onNext) onNext();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose, onPrev, onNext, hasPrev, hasNext]);

  const spamScoreTone =
    submission.spamScore > 0.7 ? 'danger' : submission.spamScore > 0.4 ? 'warning' : 'success';
  const spamNotes = submission.spamResponse || (submission as any).spam_response;

  return (
    <ModalShell
      title={`Submission #${submission.id}`}
      onClose={onClose}
      className="submission-detail-modal"
      headerPrefix={(onPrev || onNext) && (
        <div className="modal-nav-buttons">
          <button
            className="btn btn-sm btn-secondary"
            onClick={(event) => { event.stopPropagation(); onPrev?.(); }}
            disabled={!hasPrev}
            title="Previous (Left arrow)"
          >
            Prev
          </button>
          <button
            className="btn btn-sm btn-secondary"
            onClick={(event) => { event.stopPropagation(); onNext?.(); }}
            disabled={!hasNext}
            title="Next (Right arrow)"
          >
            Next
          </button>
        </div>
      )}
      footer={
        <>
          {onDelete && (
            <button className="btn btn-danger" onClick={onDelete}>
              Delete
            </button>
          )}
          <button className="btn btn-secondary" onClick={onClose}>
            Close
          </button>
        </>
      }
    >
      <div className="submission-detail">
        <section className="submission-detail-status">
          <div className="submission-detail-badges">
            <span className={`status-badge ${STATUS_CLASS[submission.status] || 'status-badge-neutral'}`}>
              {submission.status.charAt(0).toUpperCase() + submission.status.slice(1)}
            </span>
            {submission.testMode && <span className="status-badge status-badge-neutral">Test mode</span>}
          </div>
          {onStatusChange && (
            <div className="submission-detail-actions">
              {submission.status !== 'approved' && (
                <button className="btn btn-sm btn-success" onClick={() => onStatusChange('approve')}>
                  Approve
                </button>
              )}
              {submission.status !== 'flagged' && (
                <button className="btn btn-sm btn-warning" onClick={() => onStatusChange('flag')}>
                  Flag
                </button>
              )}
              {submission.status !== 'spam' && (
                <button className="btn btn-sm btn-danger" onClick={() => onStatusChange('spam')}>
                  Spam
                </button>
              )}
            </div>
          )}
        </section>

        <section className="submission-detail-meta">
          <DetailField label="Form" value={formName} />
          <DetailField
            label="Spam score"
            value={`${(submission.spamScore * 100).toFixed(0)}%`}
            tone={spamScoreTone}
          />
          <DetailField label="Submitted" value={formatDate(submission.createdAt)} />
          <DetailField label="Processed" value={formatDate(submission.processedAt)} />
          <DetailField label="IP address" value={submission.submittingIp || '-'} mono />
          <DetailField label="Origin" value={submission.origin || '-'} mono />
        </section>

        {spamNotes && (
          <section className="submission-detail-section">
            <h3>AI Notes</h3>
            <pre className="submission-detail-notes">{spamNotes}</pre>
          </section>
        )}

        <section className="submission-detail-section submission-detail-data-section">
          <h3>Submitted Data</h3>
          <div className="submission-data-table-wrap">
            {Object.entries(submission.data).length === 0 ? (
              <div className="submission-detail-empty">No data submitted</div>
            ) : (
              <table className="submission-data-table">
                <tbody>
                  {Object.entries(submission.data).map(([key, value]) => (
                    <tr key={key}>
                      <th>{key}</th>
                      <td>
                        {typeof value === 'object' ? (
                          <pre>{JSON.stringify(value, null, 2)}</pre>
                        ) : (
                          String(value)
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </section>
      </div>
    </ModalShell>
  );
};

function DetailField({
  label,
  value,
  mono,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  tone?: 'success' | 'warning' | 'danger';
}) {
  return (
    <div className="detail-field">
      <span>{label}</span>
      <strong className={`${mono ? 'mono-cell' : ''} ${tone ? `tone-${tone}` : ''}`}>{value}</strong>
    </div>
  );
}

export default SubmissionDetail;
